/**
 * Renders the verdict / confidence / snippets returned by the validate-asset
 * endpoint. Advisory only — the resolve action gates on toggle / activation
 * consent, not on the verdict, so even an `unavailable` or `not_relevant`
 * result still lets the user proceed via the ResolveConfirmationPanel that
 * sits below this component in the confirm view.
 */
import { AlertTriangle, CheckCircle2, MinusCircle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type {
  KGAssetValidationResponse,
  KGValidationVerdict,
} from '@/lib/api/knowledgeGaps';

const VERDICT_META: Record<
  KGValidationVerdict,
  { label: string; className: string; Icon: typeof CheckCircle2 }
> = {
  addresses: {
    label: 'Addresses the gap',
    className:
      'border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    Icon: CheckCircle2,
  },
  partial: {
    label: 'Partially addresses the gap',
    className:
      'border-amber-500/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
    Icon: AlertTriangle,
  },
  not_relevant: {
    label: 'Does not address the gap',
    className:
      'border-rose-500/40 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300',
    Icon: XCircle,
  },
};

export function ValidationResultPanel({
  result,
}: {
  result: KGAssetValidationResponse;
}) {
  const isUnavailable = result.status === 'unavailable';

  return (
    <div className="flex flex-col gap-5">
      {isUnavailable ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-3 dark:bg-amber-950/30">
          <MinusCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800 dark:text-amber-200 space-y-1">
            <p className="font-medium">Validation unavailable</p>
            <p className="text-xs">
              We couldn't run automatic validation just now. You can still
              proceed if you're confident this source addresses the gap.
            </p>
          </div>
        </div>
      ) : (
        <VerdictHeader result={result} />
      )}

      {!isUnavailable && result.snippets.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Relevant excerpts</h4>
          <ul className="space-y-2">
            {result.snippets.map((snippet, i) => (
              <li
                key={i}
                className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1"
              >
                {snippet.source_location && (
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {snippet.source_location}
                  </div>
                )}
                <p className="leading-relaxed">{snippet.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isUnavailable && result.rationale && (
        <div className="space-y-1.5">
          <h4 className="text-sm font-semibold">Reviewer notes</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {result.rationale}
          </p>
        </div>
      )}
    </div>
  );
}

function VerdictHeader({ result }: { result: KGAssetValidationResponse }) {
  if (!result.verdict) {
    return null;
  }
  const meta = VERDICT_META[result.verdict];
  const confidencePct =
    result.confidence !== null
      ? `${Math.round(result.confidence * 100)}%`
      : null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className={`gap-1.5 ${meta.className}`}
        >
          <meta.Icon className="h-3.5 w-3.5" />
          {meta.label}
        </Badge>
        {confidencePct && (
          <span className="text-xs text-muted-foreground">
            Confidence: {confidencePct}
          </span>
        )}
      </div>
    </div>
  );
}
